import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as R from 'remeda';
import { UniqueIdentifier } from '@dnd-kit/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { produce } from 'immer';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as z from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import {
  isSchemaTableMultiValue,
  Schema,
  SchemaItem,
} from '@rossum/api-client/schemas';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { SchemaFieldIcon } from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import { IconCirclePlus } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { CustomTextField } from '../../../../components/ReactHookForm/controls/CustomTextField';
import SwitchControl from '../../../../components/ReactHookForm/controls/SwitchControl';
import TextFieldControl from '../../../../components/ReactHookForm/controls/TextFieldControl';
import FormTypeahead from '../../../../components/ReactHookForm/FormTypeahead';
import { EXTRACTED_FIELD_TYPES_LINK } from '../../../../constants/values';
import EnumEditor from '../../../../containers/Queue/containers/QueueSchema/components/EnumEditor';
import { boldText, linebreak, link } from '../../../../lib/formaterValues';
import { parseJsonPreprocessor } from '../../../../lib/parseJsonPreprocessor';
import { restrictedEngineEditEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import { AutocompleteGroup } from '../../../engines/components/autocomplete/AutocompleteGroup';
import { CreateFieldDialogContent } from '../../../engines/components/CreateFieldDialogContent';
import { useEngine } from '../../../engines/hooks/useEngine';
import { useUnpaginatedEngineFields } from '../../../engines/hooks/useUnpaginatedEngineFields';
import { isValidFieldId } from '../../../formulas/FormulaEditor/helpers';
import { updateFieldFormula } from '../../../formulas/FormulaFieldSection/helpers/updateFieldFormula';
import { FormulaPreviewTile } from '../../../formulas/FormulaPreview/FormulaPreviewTile';
import { getFlattenSchemaFields } from '../../../schemas/getFlattenSchemaFields';
import {
  dataTypeOptions,
  dateFieldFormatOptions,
  DELETE_FIELD_FORM_ID,
  DUPLICATE_FIELD_FORM_ID,
  EditingOption,
  editingOptions,
  FIELD_FORM_ID,
  formulaEditingOptions,
  IDENTIFICATION_SECTION_LINK,
  legacyValueSourceOptions,
  multivalueDataTypeOptions,
  numberFieldFormatOptions,
  properValueSourceOptions,
  uiValidFieldTypeOptions,
  uiValidLineItemFieldTypeOptions,
} from '../constants';
import { FORM_ROOT_KEY } from '../form-model/errors/errorParsers';
import { fieldsFormErrorMap } from '../form-model/errors/formErrorMap';
import { useValidateFormErrors } from '../form-model/errors/useValidateFormErrors';
import {
  FieldsFormModel,
  fieldsFormSchema,
  FieldsFormValues,
} from '../form-model/formModels';
import {
  isComputedFieldFormModel,
  isLookupFieldFormModel,
} from '../form-model/formModels.guards';
import { toFormValues } from '../form-model/transformations';
import {
  isSchemaItemChildPath,
  retrieveFromSchema,
} from '../form-model/transformations/toSchema.utils';
import { generateSchemaId } from '../generateSchemaId';
import {
  getChildListPath,
  getSchemaObjectPaths,
  resolveIconTypeForFieldType,
} from '../helpers';
import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import { getUnsafeSchemaConcept } from '../hooks/useSchemaContept';
import { CustomLookup } from './custom-lookup/CustomLookup';
import { customMatchingConfigurationSchema } from './custom-lookup/utils';
import { CustomMemo } from './custom-memo/CustomMemo';
import { FieldChildren, FieldChildrenProps } from './FieldChildren';
import { AiEngineSection } from './form/AiEngineSection';
import {
  isCanObtainTokenAvailable,
  isChildrenAvailable,
  isDataTypeAvailable,
  isEditingAvailable,
  isFieldFromEngine,
  isFormatAvailable,
  isFormulaEditorAvailable,
  isMemoryConfigAvailable,
  isOptionsAvailable,
  isReasoningAvailable,
  isTabularAvailable,
  isUrlAvailable,
  isValueSourceAvailable,
} from './form/conditions';
import { ContentOption } from './form/ContentOption';
import { FormulaSection } from './form/FormulaSection';
import { PreFillFormSection } from './form/PreFillFormSection';
import { QuickActionIcon } from './form/QuickActionIcon';
import { ReasoningTiles } from './ReasoningTiles';

const supportedGroups = ['used', 'unused'] as const;

export type ItemContentProps = {
  data: SchemaItem | null;
  onSubmit: (formModel: FieldsFormModel) => void;
  onDuplicate?: (formModel: FieldsFormModel) => void;
  onDelete: (itemId: string) => void;
  onChildrenReorder: (
    from: readonly [UniqueIdentifier, number],
    to: readonly [UniqueIdentifier, number]
  ) => void;
  onQuickAction: (
    parentId: UniqueIdentifier,
    items: Record<
      UniqueIdentifier,
      {
        prop: 'hidden' | 'canExport' | 'required';
        value: boolean;
      }
    >
  ) => void;
  schema: Schema;
  parentId: string;
  supportLegacyUser?: boolean;
  formulaFieldsEnabled: boolean;
  reasoningFieldsEnabled: boolean;
  lookupFieldsEnabled: boolean;
  memoryFieldsEnabled: boolean;
  queues: ItemContentQueue[];
};

export type ItemContentQueue = Pick<Queue, 'id' | 'url' | 'engine' | 'name'>;

export const ItemContent = ({
  data,
  onSubmit,
  onDuplicate,
  onDelete,
  onChildrenReorder,
  onQuickAction,
  schema,
  parentId,
  supportLegacyUser,
  formulaFieldsEnabled,
  reasoningFieldsEnabled,
  lookupFieldsEnabled,
  memoryFieldsEnabled,
  queues,
}: ItemContentProps) => {
  const intl = useIntl();

  const [rootError, setRootError] = useState<string | null>(null);
  // If we have multiple queues that share a schema, they all use the same engine by design
  // so it's okay to only look at the first one
  const auroraEngineUrl = queues[0]
    ? (queues[0].engine ?? undefined)
    : undefined;

  const auroraEngineId = auroraEngineUrl ? getIDFromUrl(auroraEngineUrl) : null;

  const isRestrictedEngineEditEnabled = useSelector(
    restrictedEngineEditEnabledSelector
  );

  const { data: auroraEngine } = useEngine(auroraEngineId ?? -1);

  const usedSchemaIds = useMemo(
    () =>
      Object.keys(getFlattenSchemaFields(schema.content ?? [])).filter(
        schemaId => schemaId !== data?.id
      ),
    [data?.id, schema.content]
  );

  const { data: engineFields, isLoading: isEngineFieldsLoading } =
    useUnpaginatedEngineFields(
      { engine: auroraEngineId ?? undefined },
      {
        enabled: typeof auroraEngineId === 'number',
        select: fields =>
          fields
            .map(({ id, name, label, type }) => ({
              id,
              name,
              label,
              type,
              used: usedSchemaIds.includes(name),
            }))
            .sort((o1, o2) => +o1.used - +o2.used),
      }
    );

  const { dialog, setDialogState } = useConfirmationDialog();

  const schemaObjectPaths = useMemo(
    () => getSchemaObjectPaths(schema),
    [schema]
  );

  const parentSchemaObjectPath = useMemo(
    () => schemaObjectPaths[parentId] ?? null,
    [parentId, schemaObjectPaths]
  );

  const itemSchemaObjectPath = useMemo(() => {
    const childListPath = parentSchemaObjectPath
      ? getChildListPath(parentSchemaObjectPath)
      : null;
    const currentSchemaObjectPath = data && schemaObjectPaths[data.id];

    if (currentSchemaObjectPath) {
      return currentSchemaObjectPath;
    }

    if (childListPath) {
      const lengthOfSiblings = retrieveFromSchema(
        schema,
        childListPath
      )?.length;

      if (lengthOfSiblings !== undefined) {
        return [...childListPath, lengthOfSiblings];
      }
    }

    return null;
  }, [data, parentSchemaObjectPath, schema, schemaObjectPaths]);

  const onValidateFail = useCallback(() => {
    skipPrompt.current = false;
  }, []);

  const { validateForm } = useValidateFormErrors({
    onValidateFail,
    schema,
    data,
    parentId,
  });

  // RHF cannot infer this from `resolver`, sheesh
  const formMethods = useForm<FieldsFormValues, undefined, FieldsFormModel>({
    defaultValues: toFormValues(data, itemSchemaObjectPath),
    resolver: zodResolver(
      fieldsFormSchema(intl).superRefine(async (formValues, ctx) => {
        const errorEntries = await validateForm(formValues);
        setRootError(null);
        if (errorEntries) {
          errorEntries.forEach(([key, message]) => {
            if (key === FORM_ROOT_KEY) {
              setRootError(message);
            }

            ctx.addIssue({ code: 'custom', path: [key], message });
          });
        }
      }),
      {
        errorMap: fieldsFormErrorMap(intl),
      }
    ),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { handleSubmit: handleDeleteFormSubmit } = useForm({
    defaultValues: {
      id: data?.id ?? null,
    },
  });

  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const formValues = getValues();

  const { field, fieldType } = watch();

  // Controlling this so we can pre-fill engine field dialog
  const [fieldIdInputValue, setFieldIdInputValue] = useState(field.id);

  // We are on an Aurora enabled queue if we have auroraEngineId + we check the field is Captured
  const isAuroraCapturedField =
    isFieldFromEngine(formValues) && typeof auroraEngineId === 'number';

  const valueSourceOptions = [
    ...properValueSourceOptions.filter(option =>
      option === 'formula'
        ? formulaFieldsEnabled
        : option === 'reasoning'
          ? reasoningFieldsEnabled
          : option === 'lookup'
            ? lookupFieldsEnabled
            : true
    ),
    ...(supportLegacyUser ? legacyValueSourceOptions : []),
  ];

  type ValueSourceOption = (typeof valueSourceOptions)[number];

  const isValueSourceOption = (val: unknown): val is ValueSourceOption =>
    valueSourceOptions.includes(val as ValueSourceOption);

  const typeOptions = isSchemaItemChildPath(itemSchemaObjectPath)
    ? uiValidLineItemFieldTypeOptions
    : uiValidFieldTypeOptions;

  type TypeOption = (typeof typeOptions)[number];

  const isTypeOption = (val: unknown): val is TypeOption =>
    uiValidLineItemFieldTypeOptions.includes(val as never) ||
    uiValidFieldTypeOptions.includes(val as never);

  const nestedItemsData = useMemo(() => {
    if (isSchemaTableMultiValue(data)) {
      return data;
    }

    return null;
  }, [data]);

  // resets on remounts and this component is being re-mounted every time `data` changes
  // not just rerendered (`key` from parent)
  const skipPrompt = useRef(false);

  const deleteHandler: SubmitHandler<{ id: string | null }> = useCallback(
    ({ id }) => {
      if (id) {
        skipPrompt.current = true;
        onDelete(id);
      }
    },
    [onDelete]
  );

  const submitHandler: SubmitHandler<FieldsFormModel> = useCallback(
    formModel => {
      if (itemSchemaObjectPath) {
        skipPrompt.current = true;
        onSubmit(formModel);
      }
    },
    [itemSchemaObjectPath, onSubmit]
  );

  const duplicateHandler: SubmitHandler<FieldsFormModel> = useCallback(
    formModel => {
      if (itemSchemaObjectPath && onDuplicate) {
        skipPrompt.current = true;
        onDuplicate(
          produce(formModel, draft => {
            draft.field.label = `${draft.field.label} (copy)`;
            draft.field.id = `${draft.field.id}_copy`;
          })
        );
      }
    },
    [itemSchemaObjectPath, onDuplicate]
  );

  const childrenReorderHandler: FieldChildrenProps['onChildrenReorder'] =
    useCallback(
      (from, to) => {
        onChildrenReorder(from, to);
      },
      [onChildrenReorder]
    );

  const defaultLookup = useMemo(
    () =>
      z
        .preprocess(parseJsonPreprocessor, customMatchingConfigurationSchema)
        .safeParse(localStorage.getItem('demoDefaultLookup')),
    []
  );

  useEffect(() => {
    if (field.valueSource === 'lookup') {
      setValue('field.dataType', 'enum');
      // Make sure to also match empty object
      if (R.isEmpty(field.matching)) {
        setValue(
          'field.matching',
          defaultLookup.success
            ? defaultLookup.data
            : {
                type: 'master_data_hub',
              }
        );
      }
    }
  }, [defaultLookup, field.matching, field.valueSource, setValue]);

  useEffect(() => {
    if (
      fieldType === 'multivalue' &&
      (field.valueSource === 'formula' || field.valueSource === 'reasoning')
    ) {
      setValue('field.valueSource', 'captured');
    }
  }, [fieldType, setValue, field.valueSource]);

  useEffect(() => {
    if (
      field.valueSource === 'captured' &&
      typeof auroraEngineId !== 'number' &&
      !isEngineFieldsLoading &&
      !engineFields?.some(f => f.name === field.id)
    ) {
      setValue('field.id', '');
    }
  }, [
    auroraEngineId,
    engineFields,
    field.id,
    field.valueSource,
    isEngineFieldsLoading,
    setValue,
  ]);

  useEffect(() => {
    if (
      field.valueSource === 'captured' &&
      typeof auroraEngineId === 'number' &&
      !isEngineFieldsLoading &&
      engineFields?.some(f => f.name === field.id)
    ) {
      const engineField = engineFields.find(f => f.name === field.id);

      if (engineField) {
        setValue('field.dataType', engineField.type);
      }
    }
  }, [
    auroraEngineId,
    field.valueSource,
    engineFields,
    field.id,
    isEngineFieldsLoading,
    setValue,
  ]);

  const [errorExpanded, setErrorExpanded] = useState(false);

  // Do not change field ID before input gets blurred.
  // Otherwise, it would fetch engine field on every keystroke.
  // TODO: This should be done better though, maybe some throttling on refetches, rather than extra state
  // it also looks weird with the dropdown variant
  const [fieldId, setFieldId] = useState(field.id);

  // Create field dialog
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);

  const schemaContent = useMemo(
    () =>
      getUnsafeSchemaConcept({
        schema,
        parentId,
        apiModel: data,
        formValues,
      })?.content ?? [],
    [data, formValues, parentId, schema]
  );

  if (!itemSchemaObjectPath) {
    return null;
  }

  const shouldDisableExportAndRequired = formValues.fieldType === 'lineItems';

  return (
    <FormProvider {...formMethods}>
      <Stack
        p={4}
        spacing={4}
        id={FIELD_FORM_ID}
        component="form"
        onSubmit={handleSubmit(submitHandler)}
      >
        {rootError ? (
          <Alert
            variant="filled"
            severity="error"
            action={
              <Button
                color="inherit"
                variant="text"
                onClick={() => setErrorExpanded(expanded => !expanded)}
                sx={{ flexShrink: 0 }}
              >
                {intl.formatMessage({
                  id: 'features.queueSettings.fields.form.errors.encountered.more',
                })}
              </Button>
            }
          >
            {intl.formatMessage({
              id: 'features.queueSettings.fields.form.errors.encountered',
            })}
            <Collapse in={errorExpanded}>
              <pre>{rootError}</pre>
            </Collapse>
          </Alert>
        ) : null}
        <Stack direction="row" spacing={4}>
          <ContentOption
            title={intl.formatMessage({
              id: 'features.queueSettings.fields.form.visible.label',
            })}
            helperText={intl.formatMessage({
              id: 'features.queueSettings.fields.form.visible.helperText',
            })}
            icon={<QuickActionIcon fieldKey="hidden" state={!field.hidden} />}
          >
            <SwitchControl
              ControllerProps={{ control, name: 'field.hidden' }}
              label=""
              showInvertedValue
            />
          </ContentOption>
          <ContentOption
            title={intl.formatMessage({
              id: 'features.queueSettings.fields.form.exported.label',
            })}
            helperText={intl.formatMessage({
              id: 'features.queueSettings.fields.form.exported.helperText',
            })}
            icon={
              <QuickActionIcon
                fieldKey="exported"
                state={!!field.exported}
                disabled={shouldDisableExportAndRequired}
              />
            }
          >
            <SwitchControl
              ControllerProps={{ control, name: 'field.exported' }}
              label=""
              disabled={shouldDisableExportAndRequired}
              tooltipTitle={
                shouldDisableExportAndRequired
                  ? intl.formatMessage({
                      id: 'features.queueSettings.fields.form.exported.disabledTooltip',
                    })
                  : undefined
              }
            />
          </ContentOption>
          <ContentOption
            title={intl.formatMessage({
              id: 'features.queueSettings.fields.form.required.label',
            })}
            helperText={intl.formatMessage({
              id: 'features.queueSettings.fields.form.required.helperText',
            })}
            icon={
              <QuickActionIcon
                fieldKey="required"
                state={!!field.required}
                disabled={shouldDisableExportAndRequired}
              />
            }
          >
            <SwitchControl
              ControllerProps={{ control, name: 'field.required' }}
              label=""
              disabled={shouldDisableExportAndRequired}
              tooltipTitle={
                shouldDisableExportAndRequired
                  ? intl.formatMessage({
                      id: 'features.queueSettings.fields.form.required.disabledTooltip',
                    })
                  : undefined
              }
            />
          </ContentOption>
        </Stack>
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.queueSettings.fields.form.type.title',
          })}
          description={intl.formatMessage(
            {
              id: 'features.queueSettings.fields.form.type.description',
            },
            {
              linebreak,
              link: link(IDENTIFICATION_SECTION_LINK),
            }
          )}
        >
          <TextFieldControl
            autoFocus
            ControllerProps={{ control, name: 'fieldType' }}
            label={intl.formatMessage({
              id: 'features.queueSettings.fields.form.fieldType.label',
            })}
            select
            autoComplete="off"
            FieldLabelProps={{
              layout: 'floating',
            }}
            SelectProps={{
              renderValue: val => {
                if (isTypeOption(val)) {
                  const iconType = resolveIconTypeForFieldType(val);

                  return (
                    <Stack direction="row" spacing={1} alignItems="center">
                      {iconType ? <SchemaFieldIcon type={iconType} /> : null}
                      <Typography>
                        {intl.formatMessage({
                          id: `features.queueSettings.fields.form.fieldType.option.${val}`,
                        })}
                      </Typography>
                    </Stack>
                  );
                }

                return null;
              },
            }}
          >
            {typeOptions.map(o => {
              const iconType = resolveIconTypeForFieldType(o);

              return (
                <MenuItem key={o} value={o} sx={{ maxWidth: '450px' }}>
                  {iconType ? (
                    <ListItemIcon>
                      <SchemaFieldIcon type={iconType} />
                    </ListItemIcon>
                  ) : null}
                  <ListItemText
                    primary={intl.formatMessage({
                      id: `features.queueSettings.fields.form.fieldType.option.${o}`,
                    })}
                    secondary={intl.formatMessage({
                      id: `features.queueSettings.fields.form.fieldType.option.${o}.helperText`,
                    })}
                    secondaryTypographyProps={{
                      sx: {
                        textWrap: 'wrap',
                      },
                    }}
                  />
                </MenuItem>
              );
            })}
          </TextFieldControl>
          {isValueSourceAvailable(formValues) && (
            <Controller
              control={control}
              name="field.valueSource"
              render={FieldProps => (
                <CustomTextField
                  FieldProps={{
                    ...FieldProps,
                    field: {
                      ...FieldProps.field,
                      onChange: e => {
                        FieldProps.field.onChange(e);
                        setValue(
                          'field.editing',
                          e.target.value === 'formula' ||
                            e.target.value === 'reasoning'
                            ? 'disabled'
                            : 'enabled'
                        );

                        if (e.target.value === 'reasoning') {
                          setValue('field.context', ['self.attr.label']);
                        }
                      },
                    },
                  }}
                  data-cy="field-value-source-select"
                  label={intl.formatMessage({
                    id: 'features.queueSettings.fields.form.valueSource.label',
                  })}
                  select
                  autoComplete="off"
                  FieldLabelProps={{
                    layout: 'floating',
                  }}
                  SelectProps={{
                    renderValue: val => {
                      if (isValueSourceOption(val)) {
                        return (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <SchemaFieldIcon type={val} />
                            <Typography>
                              {intl.formatMessage({
                                id: `features.queueSettings.fields.form.valueSource.option.${val}`,
                              })}
                            </Typography>
                          </Stack>
                        );
                      }
                      return null;
                    },
                  }}
                >
                  {valueSourceOptions.map(o => {
                    // TODO: get correct options from the one place and remove this condition from here
                    // formula is not supported for multivalue fields
                    // neither is reasoning
                    // neither is lookup
                    if (
                      fieldType === 'multivalue' &&
                      (o === 'formula' || o === 'reasoning' || o === 'lookup')
                    ) {
                      return null;
                    }

                    return (
                      <MenuItem
                        key={o}
                        value={o}
                        data-cy="field-value-source-option"
                        data-id={o}
                        sx={{ maxWidth: '450px' }}
                      >
                        <ListItemIcon>
                          <SchemaFieldIcon type={o} />
                        </ListItemIcon>
                        <ListItemText
                          primary={intl.formatMessage({
                            id: `features.queueSettings.fields.form.valueSource.option.${o}`,
                          })}
                          secondary={intl.formatMessage({
                            id: `features.queueSettings.fields.form.valueSource.option.${o}.helperText`,
                          })}
                          secondaryTypographyProps={{
                            sx: {
                              textWrap: 'wrap',
                            },
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                </CustomTextField>
              )}
            />
          )}
          {isEditingAvailable(formValues) && (
            <TextFieldControl
              ControllerProps={{ control, name: 'field.editing' }}
              label={intl.formatMessage({
                id: 'features.queueSettings.fields.form.editing.label',
              })}
              select
              autoComplete="off"
              FieldLabelProps={{
                layout: 'floating',
              }}
              SelectProps={{
                // @ts-expect-error
                renderValue: (val: EditingOption) => (
                  <Typography>
                    {intl.formatMessage({
                      id: `features.queueSettings.fields.form.editing.option.${val}`,
                    })}
                  </Typography>
                ),
              }}
            >
              {(field.valueSource === 'formula' ||
              field.valueSource === 'reasoning'
                ? formulaEditingOptions
                : editingOptions
              ).map(o => (
                <MenuItem key={o} value={o} sx={{ maxWidth: '450px' }}>
                  <ListItemText
                    primary={intl.formatMessage({
                      id: `features.queueSettings.fields.form.editing.option.${o}`,
                    })}
                    secondary={intl.formatMessage(
                      {
                        id: `features.queueSettings.fields.form.editing.option.${o}.helperText`,
                      },
                      {
                        linebreak,
                      }
                    )}
                    secondaryTypographyProps={{
                      sx: {
                        textWrap: 'wrap',
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </TextFieldControl>
          )}
          {isUrlAvailable(formValues) && (
            <TextFieldControl
              ControllerProps={{ control, name: 'field.url' }}
              label={intl.formatMessage({
                id: 'features.queueSettings.fields.form.url.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              helperText={intl.formatMessage({
                id: 'features.queueSettings.fields.form.url.helperText',
              })}
            />
          )}
          {isCanObtainTokenAvailable(formValues) && (
            <SwitchControl
              ControllerProps={{ control, name: 'field.canObtainToken' }}
              label={intl.formatMessage({
                id: 'features.queueSettings.fields.form.canObtainToken.label',
              })}
            />
          )}
        </ContentGroup>
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.queueSettings.fields.form.identification.title',
          })}
          description={intl.formatMessage({
            id: 'features.queueSettings.fields.form.identification.description',
          })}
        >
          <TextFieldControl
            ControllerProps={{ control, name: 'field.label' }}
            data-cy="field-label-input"
            label={intl.formatMessage({
              id: 'features.queueSettings.fields.form.label.label',
            })}
            FieldLabelProps={{
              layout: 'floating',
            }}
            onBlur={() => {
              if (formValues.field.id === '' && !isAuroraCapturedField) {
                setValue('field.id', generateSchemaId(formValues.field.label));
                formMethods.trigger('field.id');
              }
            }}
          />
          {isAuroraCapturedField &&
          formValues.fieldType !== 'button' &&
          formValues.fieldType !== 'lineItems' ? (
            <FormTypeahead
              id="field.id"
              name="field.id"
              control={control}
              options={engineFields ?? []}
              getOptionLabel={option =>
                typeof option === 'string' ? option : option?.name
              }
              label={intl.formatMessage({
                id: `features.queueSettings.fields.form.id.label`,
              })}
              helperText={
                isValidFieldId(formValues.field.id)
                  ? ''
                  : intl.formatMessage({
                      id: 'features.queueSettings.fields.form.id.helperText',
                    })
              }
              FormHelperTextProps={
                isValidFieldId(formValues.field.id)
                  ? undefined
                  : { sx: { color: t => t.palette.warning.main } }
              }
              disablePortal
              disableClearable
              autoSelect
              groupBy={option =>
                !usedSchemaIds.includes(option.name) ? 'unused' : 'used'
              }
              renderGroup={params => {
                const group = supportedGroups.find(
                  group => group === params.group
                );
                return (
                  <Fragment key={params.key}>
                    {group ? (
                      <AutocompleteGroup>
                        {intl.formatMessage({
                          id: `features.queueSettings.fields.form.id.group.${group}`,
                        })}
                      </AutocompleteGroup>
                    ) : null}
                    {params.children}
                  </Fragment>
                );
              }}
              getOptionValue={option => option.name}
              onChange={(_, __, ___, option) => {
                if (option?.option.name) {
                  setValue('field.id', option?.option.name);
                  formMethods.trigger('field.id');
                }
              }}
              inputValue={fieldIdInputValue}
              onInputChange={(_, newValue) => setFieldIdInputValue(newValue)}
              renderOption={(props, option) => (
                <MenuItem {...props} key={option.name} disabled={option.used}>
                  {option.name}
                </MenuItem>
              )}
              size="small"
              PaperComponent={({ children }) => (
                <Paper>
                  {children}
                  {isRestrictedEngineEditEnabled ? null : (
                    <ListItem sx={{ justifyContent: 'center' }}>
                      <Button
                        color="secondary"
                        startIcon={
                          <SvgIcon fontSize="large">
                            <IconCirclePlus />
                          </SvgIcon>
                        }
                        onMouseDown={() => setFieldDialogOpen(true)}
                        data-cy="create-engine-field-button"
                      >
                        {intl.formatMessage({
                          id: 'features.queueSettings.fields.form.id.newFieldLabel',
                        })}
                      </Button>
                    </ListItem>
                  )}
                </Paper>
              )}
            />
          ) : (
            <Controller
              control={control}
              name="field.id"
              render={FieldProps => (
                <CustomTextField
                  FieldProps={{
                    ...FieldProps,
                    field: {
                      ...FieldProps.field,
                      onBlur: () => {
                        FieldProps.field.onBlur();
                        setFieldId(formValues.field.id);
                      },
                    },
                  }}
                  label={intl.formatMessage({
                    id: 'features.queueSettings.fields.form.id.label',
                  })}
                  helperText={
                    isValidFieldId(formValues.field.id)
                      ? ''
                      : intl.formatMessage({
                          id: 'features.queueSettings.fields.form.id.helperText',
                        })
                  }
                  FormHelperTextProps={
                    isValidFieldId(formValues.field.id)
                      ? undefined
                      : { sx: { color: t => t.palette.warning.main } }
                  }
                  FieldLabelProps={{
                    layout: 'floating',
                  }}
                />
              )}
            />
          )}
          <TextFieldControl
            ControllerProps={{ control, name: 'field.description' }}
            label={intl.formatMessage({
              id: 'features.queueSettings.fields.form.description.label',
            })}
            FieldLabelProps={{
              layout: 'floating',
            }}
          />
          {isDataTypeAvailable(formValues) && (
            <TextFieldControl
              ControllerProps={{ control, name: 'field.dataType' }}
              label={intl.formatMessage({
                id: 'features.queueSettings.fields.form.dataType.label',
              })}
              disabled={isAuroraCapturedField}
              select
              autoComplete="off"
              FieldLabelProps={{
                layout: 'floating',
              }}
            >
              {(formValues.fieldType === 'multivalue'
                ? multivalueDataTypeOptions
                : dataTypeOptions
              ).map(o => (
                <MenuItem key={o} value={o}>
                  {intl.formatMessage({
                    id: `features.queueSettings.fields.form.dataType.option.${o}`,
                  })}
                </MenuItem>
              ))}
            </TextFieldControl>
          )}
          {isFormatAvailable(formValues) && (
            <FormTypeahead
              name="field.format"
              control={control}
              options={
                field.dataType === 'number'
                  ? numberFieldFormatOptions
                  : dateFieldFormatOptions
              }
              getOptionLabel={option =>
                typeof option === 'string' ? option : option?.label
              }
              label={intl.formatMessage({
                id: `features.queueSettings.fields.form.format.label`,
              })}
              disablePortal
              autoSelect
              freeSolo
              onInputChange={(_, value) => setValue('field.format', value)}
              getOptionValue={option => option.value}
              helperText={
                (field.dataType === 'number'
                  ? numberFieldFormatOptions
                  : dateFieldFormatOptions
                ).find(o => o.value === field.format)?.helpText ?? ''
              }
              renderOption={(props, option) => (
                <MenuItem {...props} key={option.value}>
                  {option.label}
                </MenuItem>
              )}
              size="small"
            />
          )}
        </ContentGroup>

        {isLookupFieldFormModel(formValues) && (
          <Controller
            control={control}
            name="field.matching"
            render={({ field: f }) => (
              <CustomLookup
                field={f}
                schemaContent={schemaContent}
                control={control}
              />
            )}
          />
        )}

        <AiEngineSection
          engineUrl={auroraEngineUrl}
          control={control}
          formValues={formValues}
          fieldId={
            // for the select variant, relying on the "debounce-faking" state does not make sense
            // the UI should update immediately, not just after blur
            isFieldFromEngine(formValues) && typeof auroraEngineId === 'number'
              ? field.id
              : fieldId
          }
        />
        <PreFillFormSection control={control} formValues={formValues} />
        {isOptionsAvailable(formValues) && (
          <ContentGroup
            title={intl.formatMessage({
              id: 'features.queueSettings.fields.form.options.title',
            })}
            description={intl.formatMessage(
              {
                id: 'features.queueSettings.fields.form.options.description',
              },
              {
                boldText,
                linebreak,
                link: link(EXTRACTED_FIELD_TYPES_LINK),
              }
            )}
          >
            <Controller
              control={control}
              name="field.options"
              render={({ field: { value, onChange } }) => (
                <EnumEditor
                  options={value || []}
                  onCurrentSchemaPartChange={options => onChange(options)}
                />
              )}
            />
          </ContentGroup>
        )}

        {isReasoningAvailable(formValues) ? (
          <ReasoningTiles control={control} schemaContent={schemaContent} />
        ) : null}

        {isFormulaEditorAvailable(formValues) && (
          <FormulaSection control={control} schemaContent={schemaContent} />
        )}

        {isComputedFieldFormModel(formValues) &&
        isMemoryConfigAvailable(formValues) &&
        memoryFieldsEnabled ? (
          <Controller
            control={control}
            name="field.memory"
            render={({ field: f }) => (
              <CustomMemo
                field={f}
                schemaContent={schemaContent}
                control={control}
              />
            )}
          />
        ) : null}

        {isComputedFieldFormModel(formValues) && (
          <FormulaPreviewTile
            schemaContentDraft={
              formValues.field.valueSource === 'formula'
                ? updateFieldFormula(
                    schemaContent,
                    field.id,
                    field.formula ?? ''
                  )
                : schemaContent
            }
            schemaRulesDraft={[]}
            currentFormulaId={field.id}
            queueUrls={queues.map(({ url }) => url)}
            queue={queues.length === 1 ? queues[0] : undefined}
            type={formValues.field.valueSource}
          />
        )}
      </Stack>

      {nestedItemsData && isChildrenAvailable(formValues) ? (
        <FieldChildren
          parentId={field.id}
          data={nestedItemsData.children.children}
          onChildrenReorder={childrenReorderHandler}
          onQuickAction={onQuickAction}
          onItemDelete={onDelete}
        />
      ) : null}
      <Stack
        display="none"
        component="form"
        id={DELETE_FIELD_FORM_ID}
        onSubmit={e => {
          e.preventDefault();
          setDialogState({
            key: 'deleteSchemaField',
            onConfirm: () => handleDeleteFormSubmit(deleteHandler)(e),
          });
        }}
      />
      <Stack
        display="none"
        component="form"
        id={DUPLICATE_FIELD_FORM_ID}
        onSubmit={handleSubmit(duplicateHandler)}
      />
      <LeavingDialog when={!skipPrompt.current && isDirty} />
      {dialog}
      {auroraEngine ? (
        <Dialog
          open={fieldDialogOpen}
          onClose={() => setFieldDialogOpen(false)}
          sx={{ transition: 'smooth' }}
          data-cy="create-field-dialog"
        >
          {fieldDialogOpen ? (
            <CreateFieldDialogContent
              engine={auroraEngine}
              onSubmit={({ name, type }) => {
                if (name) {
                  setValue('field.id', name);
                }
                if (type) {
                  setValue('field.dataType', type);
                }
                setFieldDialogOpen(false);
              }}
              onClose={() => setFieldDialogOpen(false)}
              tabular={isTabularAvailable(formValues)}
              name={fieldIdInputValue}
              label={formValues.field.label}
            />
          ) : null}
        </Dialog>
      ) : null}
    </FormProvider>
  );
};
